/**
 * LapTimer — RaceLogic-style automatic lap timing from GPS.
 *
 * A circuit defines a finish line as two lat/lon endpoints spanning the track.
 * Each GPS fix is a point; when the segment between two consecutive fixes
 * crosses the finish-line segment (travelling roughly in the expected
 * direction), a lap is completed. Pure and testable: feed positions, read laps.
 */

export interface LatLon {
  lat: number;
  lon: number;
}

export interface Circuit {
  id: string;
  name: string;
  /** Track centre, used to auto-select the nearest circuit. */
  center: LatLon;
  /** Auto-select radius in metres (within this of center → this circuit). */
  radiusM: number;
  /** Finish line endpoints (span the tarmac, ~10-20 m apart). */
  finishA: LatLon;
  finishB: LatLon;
  /** Compass heading (deg, 0=N,90=E) the car travels when crossing S/F. */
  headingDeg: number;
  /** Typical lap length (m) — sanity floor for a valid lap. */
  lapLengthM?: number;
}

export interface LapState {
  circuitId: string | null;
  circuitName: string | null;
  onTrack: boolean;
  /** Current (in-progress) lap time in seconds, or null before the first crossing. */
  currentS: number | null;
  lastS: number | null;
  bestS: number | null;
  lapCount: number;
  /** Predictive delta to best (s), + slower / − faster; null until a best exists. */
  deltaS: number | null;
  /** With a GPS fix but no circuit in range: the closest known circuit and its distance. */
  nearestName: string | null;
  nearestKm: number | null;
}

const R_EARTH = 6371000;

/** Local equirectangular metres from a reference point (good over a few km). */
function toXY(p: LatLon, ref: LatLon): { x: number; y: number } {
  const latRad = (ref.lat * Math.PI) / 180;
  return {
    x: ((p.lon - ref.lon) * Math.PI) / 180 * R_EARTH * Math.cos(latRad),
    y: ((p.lat - ref.lat) * Math.PI) / 180 * R_EARTH,
  };
}

export function haversineM(a: LatLon, b: LatLon): number {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(s));
}

/** Segment/segment intersection with the fraction `t` along p1→p2 where it hits. */
function segmentCrossFraction(p1: LatLon, p2: LatLon, a: LatLon, b: LatLon): number | null {
  const ref = a;
  const P1 = toXY(p1, ref);
  const P2 = toXY(p2, ref);
  const A = toXY(a, ref);
  const B = toXY(b, ref);
  const r = { x: P2.x - P1.x, y: P2.y - P1.y };
  const s = { x: B.x - A.x, y: B.y - A.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 1e-9) return null; // parallel
  const qp = { x: A.x - P1.x, y: A.y - P1.y };
  const t = (qp.x * s.y - qp.y * s.x) / denom; // along p1→p2
  const u = (qp.x * r.y - qp.y * r.x) / denom; // along a→b
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return t;
  return null;
}

function bearingDeg(from: LatLon, to: LatLon): number {
  const y = toXY(to, from);
  return (Math.atan2(y.x, y.y) * 180) / Math.PI; // 0=N, 90=E
}

function angleDiff(a: number, b: number): number {
  let d = Math.abs(((a - b + 540) % 360) - 180);
  return d;
}

export function pickCircuit(pos: LatLon, circuits: Circuit[]): Circuit | null {
  let best: Circuit | null = null;
  let bestD = Infinity;
  for (const c of circuits) {
    const d = haversineM(pos, c.center);
    if (d <= c.radiusM && d < bestD) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

export class LapTimer {
  private circuit: Circuit | null = null;
  private prev: { pos: LatLon; t: number } | null = null;
  private lapStartT: number | null = null;
  private last: number | null = null;
  private best: number | null = null;
  private count = 0;
  private now = 0;
  /** best-lap distance/time samples for the predictive delta */
  private bestTrace: { d: number; t: number }[] = [];
  private curTrace: { d: number; t: number }[] = [];
  private curDist = 0;
  private minLapS: number;

  constructor(
    private readonly circuits: Circuit[],
    minLapS = 20,
  ) {
    this.minLapS = minLapS;
  }

  /** Advance the timer clock (call every frame so manual timing runs without GPS). */
  tick(tMs: number): void {
    if (tMs > this.now) this.now = tMs;
  }

  /** Manual controls (RaceLogic START / LAP / STOP — work with or without GPS). */
  startManual(tMs: number): void {
    this.lapStartT = tMs;
    this.now = tMs;
    this.curTrace = [];
    this.curDist = 0;
  }

  manualLap(tMs: number): void {
    this.now = tMs;
    if (this.lapStartT != null) this.onCross(tMs, true); // deliberate button press always counts
    else this.startManual(tMs);
  }

  stopManual(): void {
    this.lapStartT = null;
    this.curTrace = [];
    this.curDist = 0;
  }

  get running(): boolean {
    return this.lapStartT != null;
  }

  setCircuit(c: Circuit | null): void {
    if (c?.id === this.circuit?.id) return;
    this.circuit = c;
    this.reset();
  }

  reset(): void {
    this.prev = null;
    this.lapStartT = null;
    this.last = null;
    this.best = null;
    this.count = 0;
    this.bestTrace = [];
    this.curTrace = [];
    this.curDist = 0;
  }

  /** Feed a GPS fix (lat/lon) with a timestamp in ms. Auto-selects circuit if none set. */
  update(pos: LatLon, tMs: number): void {
    this.now = tMs;
    if (this.circuit && haversineM(pos, this.circuit.center) > this.circuit.radiusM * 1.6) { this.circuit = null; this.prev = null; }   // left the track → look again
    if (!this.circuit) this.circuit = pickCircuit(pos, this.circuits);
    const c = this.circuit;
    if (!c) {
      // off-track: keep the closest known circuit around for the Racelogic display ("42 KM → JARAMA")
      if (this.nearestAt === 0 || tMs - this.nearestAt > 5000) {
        this.nearestAt = tMs;
        let best: { name: string; km: number } | null = null;
        for (const cc of this.circuits) {
          const km = haversineM(pos, cc.center) / 1000;
          if (!best || km < best.km) best = { name: cc.name, km };
        }
        this.nearest = best;
      }
      this.prev = { pos, t: tMs };
      return;
    }
    if (this.prev) {
      const seg = haversineM(this.prev.pos, pos);
      this.curDist += seg;
      if (this.lapStartT != null) this.curTrace.push({ d: this.curDist, t: (tMs - this.lapStartT) / 1000 });
      const frac = segmentCrossFraction(this.prev.pos, pos, c.finishA, c.finishB);
      if (frac != null) {
        const heading = bearingDeg(this.prev.pos, pos);
        if (angleDiff(heading, c.headingDeg) <= 90) {
          const crossT = this.prev.t + (tMs - this.prev.t) * frac;
          this.onCross(crossT);
        }
      }
    }
    this.prev = { pos, t: tMs };
  }

  private onCross(crossT: number, force = false): void {
    if (this.lapStartT != null) {
      const lap = (crossT - this.lapStartT) / 1000;
      if (force || lap >= this.minLapS) {
        this.last = lap;
        this.count++;
        if (this.best == null || lap < this.best) {
          this.best = lap;
          this.bestTrace = this.curTrace.slice();
        }
      }
    }
    this.lapStartT = crossT;
    this.curTrace = [];
    this.curDist = 0;
  }

  /** Predictive delta: compare elapsed time at the current distance vs the best lap. */
  private predictiveDelta(): number | null {
    if (this.best == null || this.bestTrace.length < 2 || this.lapStartT == null) return null;
    const curElapsed = (this.now - this.lapStartT) / 1000;
    const d = this.curDist;
    // time the best lap took to reach distance d (linear interp)
    const bt = this.bestTrace;
    if (d <= bt[0].d) return curElapsed - bt[0].t;
    for (let i = 1; i < bt.length; i++) {
      if (d <= bt[i].d) {
        const f = (d - bt[i - 1].d) / Math.max(1e-6, bt[i].d - bt[i - 1].d);
        const bestTAtD = bt[i - 1].t + f * (bt[i].t - bt[i - 1].t);
        return curElapsed - bestTAtD;
      }
    }
    return curElapsed - bt[bt.length - 1].t;
  }

  private nearest: { name: string; km: number } | null = null;
  private nearestAt = 0;

  getState(): LapState {
    return {
      circuitId: this.circuit?.id ?? null,
      circuitName: this.circuit?.name ?? null,
      onTrack: this.circuit != null,
      currentS: this.lapStartT != null ? (this.now - this.lapStartT) / 1000 : null,
      lastS: this.last,
      bestS: this.best,
      lapCount: this.count,
      deltaS: this.predictiveDelta(),
      nearestName: this.circuit ? null : this.nearest?.name ?? null,
      nearestKm: this.circuit ? null : this.nearest ? Math.round(this.nearest.km * 10) / 10 : null,
    };
  }
}

export function formatLap(s: number | null): string {
  if (s == null) return '--:--.--';
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return `${m}:${sec.toFixed(2).padStart(5, '0')}`;
}

export function formatDelta(s: number | null): string {
  if (s == null) return '';
  const sign = s > 0 ? '+' : s < 0 ? '−' : '';
  return `${sign}${Math.abs(s).toFixed(2)}`;
}
